import React, { useEffect, useState } from "react";
import RelationshipDrawer from "../RelationshipDrawer";
import CommonSelect from "@/shared/components/ui/Select/CommonSelect";
import CommonDatePicker from "@/shared/components/ui/DatePicker/CommonDatePicker";
import CommonTextArea from "@/shared/components/ui/Input/CommonTextArea";
import {
  TOUCH_POINT_TYPE_OPTIONS,
  WHOM_TO_REMIND_OPTIONS,
} from "@/shared/utils/accountConfig";
import {
  useAddFollowUpReminder,
  useUpdateFollowUpReminder,
} from "../../hooks/useAccountRelationship";

const FOLLOW_UP_CHANNEL_OPTIONS = TOUCH_POINT_TYPE_OPTIONS;

const FollowUpReminderDrawer = ({
  isOpen,
  onClose,
  drawerType,
  relationshipMappingId,
  followUpToEdit = null,
}) => {
  const isEditing = !!followUpToEdit;

  const [reminderFor, setReminderFor] = useState("call");
  const [scheduleDate, setScheduleDate] = useState(null);
  const [remindWhom, setRemindWhom] = useState("me");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (!isOpen) return;

    if (followUpToEdit) {
      setReminderFor(followUpToEdit.channel || "call");
      setScheduleDate(followUpToEdit.scheduled_at || null);
      setRemindWhom("me");
      setNotes(followUpToEdit.notes || "");
      return;
    }

    setReminderFor("call");
    setScheduleDate(null);
    setRemindWhom("me");
    setNotes("");
  }, [isOpen, followUpToEdit]);

  const { mutate: addFollowUpReminder, isPending: isAddingFollowUpReminder } =
    useAddFollowUpReminder({
      action: onClose,
    });

  const {
    mutate: updateFollowUpReminder,
    isPending: isUpdatingFollowUpReminder,
  } = useUpdateFollowUpReminder({
    action: onClose,
  });

  const isSaving = isAddingFollowUpReminder || isUpdatingFollowUpReminder;

  const handleSave = () => {
    const payload = {
      channel: reminderFor,
      scheduled_at: scheduleDate,
      notes: notes.trim(),
    };

    if (isEditing) {
      updateFollowUpReminder({
        followUpId: followUpToEdit.id,
        followUpReminder: payload,
        relationshipMappingId,
      });
      return;
    }

    addFollowUpReminder({
      relationshipMappingId,
      followUpReminder: payload,
    });
  };

  const isPrimaryDisabled =
    isSaving || !reminderFor || !scheduleDate || !notes.trim();

  return (
    <RelationshipDrawer
      isOpen={isOpen}
      onClose={onClose}
      drawerType={drawerType}
      title={isEditing ? "Edit Follow Up Reminder" : "Follow Up Reminder"}
      description={
        isEditing
          ? "Update the reminder details for this contact"
          : "Schedule a reminder to reconnect with this contact at the right time"
      }
      primaryButtonText={
        isEditing ? "Update Follow Up Reminder" : "Set Reminder"
      }
      secondaryButtonText="Cancel"
      isPrimaryLoading={isSaving}
      isPrimaryDisabled={isPrimaryDisabled}
      onPrimaryClick={handleSave}
      isSecondaryDisabled={isSaving}
    >
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-2 w-full">
          <h5 className="text-xs font-medium text-gray-500 font-[Inter]">
            Reminder for
          </h5>
          <CommonSelect
            options={FOLLOW_UP_CHANNEL_OPTIONS}
            value={reminderFor}
            onChange={(value) => setReminderFor(value ?? "call")}
            placeholder="Select reminder for"
            width="w-full"
            classNames={{
              trigger: "min-w-0 shrink-0",
              value: "!text-gray-800 font-inter !font-medium !text-[0.715rem]",
            }}
          />
        </div>

        <div className="flex gap-3 w-full">
          <div className="flex flex-col gap-2 w-full">
            <h5 className="text-xs font-medium text-gray-500 font-[Inter]">
              Date
            </h5>
            <CommonDatePicker
              value={scheduleDate}
              onChange={setScheduleDate}
              className="h-9"
            />
          </div>

          <div className="flex flex-col gap-2 w-full">
            <h5 className="text-xs font-medium text-gray-500 font-[Inter]">
              Whom to remind?
            </h5>
            <CommonSelect
              options={WHOM_TO_REMIND_OPTIONS}
              value={remindWhom}
              onChange={(value) => setRemindWhom(value ?? "me")}
              className="h-9"
              classNames={{
                value:
                  "!text-gray-800 font-inter !font-medium !text-[0.715rem]",
              }}
            />
          </div>
        </div>

        <div className="flex flex-col gap-2 w-full">
          <h5 className="text-xs font-medium text-gray-500 font-[Inter]">
            Notes
          </h5>
          <CommonTextArea
            placeholder="Any context or additional details for reminder..."
            rows={5}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={isSaving}
            className="disabled:opacity-100 disabled:cursor-not-allowed"
          />
        </div>
      </div>
    </RelationshipDrawer>
  );
};

export default FollowUpReminderDrawer;
